import { FC, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  HandleDragOver,
  HandleDragLeave,
  HandleDrop,
  UpdateUserLastActionDate,
  HandleLogOut,
  HandleReturn,
  PingServerWithLastActionDate,
  CollectVisibleProjectContent,
  BuildCanvasCrumbs,
  HandleCrumbSelect,
} from "../../helper/ui/methods";
import { useAppStateContext } from "../../middleware/context/state-context-provider";
import FileInfo from "../ui/file/file-info";
import ResponseDialog from "../ui/user/response-dialog";
import DecisionDialog from "../ui/user/user-binary-decision-dialog";
import ViewerOptionDialog from "../ui/user/viewer-option-dialog";
import MenuButton from "../ui/user/menu-button";
import { FileInterface } from "../../middleware/interfaces/file/file-interface";
import FolderDialog from "../ui/folder/folder-dialog";
import ForgeDialog from "../ui/forge/forge-dialog";
import { FolderInterface } from "../../middleware/interfaces/folder/folder-interface";
import FolderInfo from "../ui/folder/folder-info";
import ProjectContent from "../ui/project/project-content";
import FileVersionDialog from "../ui/file/file-version-dialog";
import MainNavigationPanel from "../ui/user/main-navigation-panel";
import MainFooterPanel from "../ui/user/main-footer-panel";
import ReportInfo from "../ui/report/report-info";
import { ReportInterface } from "../../middleware/interfaces/report/report-interface";
import ForgeInfo from "../ui/forge/forge-info";
import { ForgeInterface } from "../../middleware/interfaces/forge/forge-interface";
import { ModelRetrieveInterface } from "../../middleware/interfaces/file/model-retrieve-interface";
import ProjectContentFilterDialog from "../ui/project/project-content-filter-dialog";
import ReportDescriptionDialog from "../ui/report/report-description-dialog";
import CanvasHeader from "../ui/user/canvas-header";
import CanvasEmptyState from "../ui/user/canvas-empty-state";
import { ProjectContentInterface } from "../../middleware/interfaces/project/project-content-interface";
import "../../styles/project-page.css";

export const ProjectPage: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const [selectedContent, setSelectedContent] =
    useState<ProjectContentInterface | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [
    projectContentfilterDialogReveal,
    setProjectContentfilterDialogReveal,
  ] = useState(false);
  const [folderDialogReveal, setFolderDialogReveal] = useState("");
  const [forgeDialogReveal, setForgeDialogReveal] = useState("");
  const [fileVersionDialogReveal, setFileVersionDialogReveal] = useState(false);
  useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (state.user) {
      UpdateUserLastActionDate(state.user, dispatch);
    }
  }, [state.user]);

  useEffect(() => {
    if (state.decision) {
      if (state.decision.result !== "") {
        switch (state.decision.result) {
          case "DELETE_FOLDER":
          case "EDIT_FOLDER":
          case "DOWNLOAD_FOLDER":
          case "DELETE_FILE":
          case "DOWNLOAD_FILE":
          case "DELETE_REPORT":
          case "DOWNLOAD_REPORT":
          case "EDIT_FORGE":
          case "DELETE_FORGE":
          case "RUN_FORGE":
            dispatch({
              type: state.decision.result,
              payload: state.decision.data,
            });
            if (
              state.decision.result === "DELETE_FILE" ||
              state.decision.result === "DELETE_FOLDER" ||
              state.decision.result === "EDIT_FOLDER" ||
              state.decision.result === "DELETE_REPORT" ||
              state.decision.result === "EDIT_FORGE" ||
              state.decision.result === "DELETE_FORGE"
            ) {
              setSelectedContent(null);
            }
            break;
          case "NO_ACTION":
            break;
          case "STANDARD":
            dispatch({
              type: "RETRIEVE_MODEL",
              payload: {
                ...selectedContent,
                type: "ModelRetrieveInterface",
              } as ModelRetrieveInterface,
            });
            navigate("/STANDARD");
            break;
          case "4D":
          case "MAP":
          default:
            // Handle unknown action
            console.log(`Unknown decision result: ${state.decision.result}`);
        }
        dispatch({ type: "DECISION", decision: null });
      }
    }
  }, [state.decision?.result]);

  useEffect(() => {
    // Only a report that has just arrived. Coming Back from the report viewer
    // mounts this page while the closed report is still in state.
    if (
      state.openReport &&
      String(state.openReport.reportID) !== String(state.user?.openReport)
    ) {
      const updatedUser = {
        ...state.user!,
        openReport: state.openReport.reportID,
      };
      dispatch({
        type: "UPDATE_USER",
        payload: updatedUser,
        response: "",
      });
      //PING SERVER TO UPDATE THE SESSION
      PingServerWithLastActionDate(updatedUser!);
    }
  }, [state.openReport]);

  // Whatever moved the canvas (a folder, the path bar, Return, Back), the
  // selection belonged to the view that was left.
  useEffect(() => {
    setSelectedContent(null);
  }, [state.user?.openProject?.projectID, state.user?.openFolder?.folderID]);

  useEffect(() => {
    if (!state.user?.openProject || !selectedContent) return;

    if (
      (selectedContent.type === "FolderInterface" &&
        !state.user.openProject.projectContentFilter?.folders) ||
      (selectedContent.type === "ReportInterface" &&
        !state.user.openProject.projectContentFilter?.reports) ||
      (selectedContent.type === "ForgeInterface" &&
        !state.user.openProject.projectContentFilter?.forges) ||
      (selectedContent.type === "FileInterface" &&
        (((selectedContent as FileInterface).fileType === "ifc" &&
          !state.user.openProject.projectContentFilter?.filesAndModels
            ?.models) ||
          ((selectedContent as FileInterface).fileType !== "ifc" &&
            !state.user.openProject.projectContentFilter?.filesAndModels
              ?.files)))
    ) {
      setSelectedContent(null);
    }
  }, [state.user?.openProject?.projectContentFilter, selectedContent]);

  const fileConvasRef = useRef<HTMLDivElement>(null);

  const visibleContent = state.user
    ? CollectVisibleProjectContent(state.user)
    : [];

  return (
    <>
      {(() => {
        if (state.user?.openProject) {
          return (
            <div
              id="pageWrapper"
              onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
            >
              <MainNavigationPanel user={state.user} />
              <div id="contentWrapper">
                <div id="leftMenu">
                  <div className="menuButtonWrapper">
                    <MenuButton
                      imageSource="assets/icons/add-folder.png"
                      alternativeText="ADD FOLDER"
                      dispatch={() => {
                        setFolderDialogReveal("CREATE");
                      }}
                    />
                    {state.user.openProject?.projectUserRole === "OWNER" ? (
                      <MenuButton
                        imageSource="assets/icons/add-forge.png"
                        alternativeText="ADD FORGE"
                        dispatch={() => {
                          setForgeDialogReveal("CREATE");
                        }}
                      />
                    ) : (
                      <></>
                    )}
                    <MenuButton
                      imageSource="assets/icons/filter.png"
                      alternativeText="FILTER"
                      dispatch={() => {
                        setProjectContentfilterDialogReveal(true);
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/return.png"
                      alternativeText="RETURN"
                      dispatch={() => {
                        // setReturnPressed(true);
                        HandleReturn(state, dispatch);
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/logout.png"
                      alternativeText="LOG OUT"
                      dispatch={() => {
                        HandleLogOut(dispatch);
                      }}
                    />
                  </div>
                </div>
                <div id="content" className="canvasSurface">
                  <CanvasHeader
                    crumbs={BuildCanvasCrumbs(state.user)}
                    onCrumbSelect={(crumbIndex) => {
                      HandleCrumbSelect(state, crumbIndex, dispatch);
                    }}
                    itemCount={visibleContent.length}
                    selectedCount={selectedContent ? 1 : 0}
                  />
                  <div
                    id="projectCanvas"
                    className={isHovered ? "projectCanvasHover" : ""}
                    ref={fileConvasRef}
                    onDragOver={(event) => HandleDragOver(event, setIsHovered)}
                    onDrop={(event) => {
                      HandleDrop(
                        event,
                        state,
                        setIsHovered,
                        navigate,
                        dispatch
                      );
                    }}
                    onDragLeave={() => {
                      HandleDragLeave(setIsHovered);
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setSelectedContent(null);
                      }
                    }}
                  >
                    {visibleContent.length > 0 ? (
                      <ProjectContent
                        user={state.user}
                        selectedContent={selectedContent}
                        setSelectedContent={setSelectedContent}
                      />
                    ) : state.user.openFolder ? (
                      <CanvasEmptyState
                        imageSource="assets/icons/folder.png"
                        title="This folder is empty"
                        message="Create a folder, or drop files here to upload them."
                        primaryActionLabel="New folder"
                        primaryAction={() => {
                          setFolderDialogReveal("CREATE");
                        }}
                      />
                    ) : (
                      <CanvasEmptyState
                        imageSource="assets/icons/folder.png"
                        title="This project is empty"
                        message={
                          state.user.openProject?.projectUserRole === "OWNER"
                            ? "Create a folder, or set up a forge to pull models in from a connected source."
                            : "Create a folder to get started."
                        }
                        primaryActionLabel="New folder"
                        primaryAction={() => {
                          setFolderDialogReveal("CREATE");
                        }}
                        secondaryActionLabel={
                          state.user.openProject?.projectUserRole === "OWNER"
                            ? "New forge"
                            : undefined
                        }
                        secondaryAction={() => {
                          setForgeDialogReveal("CREATE");
                        }}
                      />
                    )}
                  </div>
                  <ViewerOptionDialog
                    decision={state.decision}
                    dispatch={dispatch}
                  />
                  <FolderDialog
                    reveal={folderDialogReveal}
                    user={state.user}
                    setFolderDialogReveal={setFolderDialogReveal}
                    selectedFolder={selectedContent as FolderInterface}
                    dispatch={dispatch}
                  />
                  <ForgeDialog
                    reveal={forgeDialogReveal}
                    user={state.user}
                    setForgeDialogReveal={setForgeDialogReveal}
                    selectedForge={selectedContent as ForgeInterface}
                    dispatch={dispatch}
                  />
                  <DecisionDialog
                    decision={state.decision}
                    dispatch={dispatch}
                  />
                  <ResponseDialog
                    title="Server Response"
                    message={state.response}
                    progress={state.uploadProgress}
                    archiveProgress={state.archiveProgress}
                    dispatch={dispatch}
                  />
                  <FileVersionDialog
                    reveal={fileVersionDialogReveal}
                    selectedFile={selectedContent as FileInterface}
                    setSelectedFile={setSelectedContent}
                    setFileVersionDialogReveal={setFileVersionDialogReveal}
                  />
                  <ProjectContentFilterDialog
                    user={state.user}
                    reveal={projectContentfilterDialogReveal}
                    setProjectContentfilterDialogReveal={
                      setProjectContentfilterDialogReveal
                    }
                    dispatch={dispatch}
                  />
                  <ReportDescriptionDialog
                    user={state.user}
                    reportDescriptionObject={state.openReportDescription}
                    selectedReport={selectedContent as ReportInterface}
                    dispatch={dispatch}
                  />
                </div>
                <div id="rightMenu">
                  {selectedContent &&
                    (selectedContent.type === "FolderInterface" ? (
                      <FolderInfo
                        user={state.user}
                        selectedFolder={selectedContent}
                        setSelectedFolder={setSelectedContent}
                        setFolderEditDialogReveal={setFolderDialogReveal}
                        dispatch={dispatch}
                      />
                    ) : selectedContent.type === "FileInterface" ? (
                      <FileInfo
                        user={state.user}
                        selectedFile={selectedContent}
                        setSelectedFile={setSelectedContent}
                        setFileVersionDialogReveal={setFileVersionDialogReveal}
                        dispatch={dispatch}
                      />
                    ) : selectedContent.type === "ReportInterface" ? (
                      <ReportInfo
                        user={state.user}
                        selectedReport={selectedContent}
                        setSelectedReport={setSelectedContent}
                        dispatch={dispatch}
                      />
                    ) : selectedContent.type === "ForgeInterface" ? (
                      <ForgeInfo
                        user={state.user}
                        selectedForge={selectedContent}
                        setSelectedForge={setSelectedContent}
                        setForgeDialogReveal={setForgeDialogReveal}
                        dispatch={dispatch}
                      />
                    ) : null)}
                </div>
              </div>
              <MainFooterPanel />
            </div>
          );
        } else if (state.user) {
          // Signed in with no project open: LocationHandler is taking the
          // address to the project list.
          return <></>;
        } else {
          return <Navigate to={"/authentication&registration"} replace />;
        }
      })()}
    </>
  );
};
